import React, { useCallback, useEffect, useId, useRef } from 'react'

import styles from './DialogWrapper.module.scss'
export const CloseButton = () => {
  // Loading the button async causes a flicker, so we load it as a static svg
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M5 15L15 5M5 5L15 15" strokeWidth="1.67" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

interface DialogWrapperProps {
  isOpen: boolean
  title: string | React.ReactNode
  subtitle?: string
  minWidth?: number | string
  close: () => void
  children: React.ReactNode
  // Hero image/video rendered above the header, flush to rounded top corners (Figma `Dialog/Image fill on top`). Pass `<img>`/`<video>` directly — bleed styles target the direct child.
  topMedia?: React.ReactNode
}

const DialogWrapper: React.FC<DialogWrapperProps> = (props: DialogWrapperProps) => {
  const { isOpen, title, subtitle, close, children, minWidth, topMedia } = props

  const overlayDialogRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  const titleId = useId()

  const handleOverlayClick = useCallback(
    (event: React.MouseEvent) => {
      // Note: overlayDialogRef is the background overlay of the dialog and not the dialog itself.
      if (event.target === overlayDialogRef.current) {
        close()
      }
    },
    [close]
  )

  const focusables = useCallback(
    () =>
      Array.from(
        dialogRef.current?.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) ?? []
      ).filter((el) => el.offsetParent !== null),
    []
  )

  // Keyed on isOpen alone: callers pass inline `close`, so including it here would
  // re-run the focus move on every render and fight with whatever the user is typing in.
  useEffect(() => {
    if (!isOpen) return
    const previouslyFocused = document.activeElement as HTMLElement | null
    dialogRef.current?.focus()
    return () => previouslyFocused?.focus()
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        close()
        return
      }
      // aria-modal tells AT the page is inert, so Tab must not be able to leave.
      if (event.key !== 'Tab') return

      const targets = focusables()
      if (targets.length === 0) {
        event.preventDefault()
        return
      }

      const first = targets[0]
      const last = targets[targets.length - 1]
      const active = document.activeElement
      if (!dialogRef.current?.contains(active)) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
      } else if (event.shiftKey && active === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, close, focusables])

  return (
    <>
      {isOpen && (
        <div ref={overlayDialogRef} className={styles.overlayDialog} onClick={handleOverlayClick}>
          <div
            ref={dialogRef}
            className={[styles.dialogContainer, topMedia ? styles.withTopMedia : ''].filter(Boolean).join(' ')}
            style={{ minWidth }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            tabIndex={-1}
          >
            {topMedia && (
              <div className={styles.topMedia}>
                {topMedia}
                <button
                  type="button"
                  className={`${styles.cancelButton} ${styles.floating}`}
                  onClick={close}
                  aria-label="Close"
                >
                  <CloseButton />
                </button>
              </div>
            )}
            <div className={styles.header}>
              <div className={styles.headerContent}>
                <div className={styles.title} id={titleId}>
                  {title}
                </div>
                {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
              </div>
              {!topMedia && (
                <button type="button" className={styles.cancelButton} onClick={close} aria-label="Close">
                  <CloseButton />
                </button>
              )}
            </div>
            {children}
          </div>
        </div>
      )}
    </>
  )
}

export interface DialogProps {
  isOpen: boolean
  setOpen: (value: boolean) => void
}

export default DialogWrapper
